import React, { useEffect, useRef, useState } from 'react';
import { Button, Alert, Form } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { setIsPlaying, setCurrentBpm, updatePartSettings, updateMetronomeSettings } from '../store/appSlice';
import { AudioEngine, MetronomeEngine } from '../services/audioEngine';
import { YouTubeEngine, nextYouTubeBpm, snapToYouTubeRate, YOUTUBE_RATES } from '../services/youtubeEngine';
import * as localFileStore from '../services/localFileStore';
import YouTubePlayer from './YouTubePlayer';
import { Track } from '../types';

interface PlayerProps {
  tracks: Track[];
}

const formatMmSs = (s: number): string => {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, '0');
  return `${m}:${sec}`;
};

/**
 * Validate a part's start/stop times against the actual audio duration.
 * Returns the (possibly clamped) range or an error message. A duration of 0
 * means "unknown" and skips validation.
 */
const validatePartRange = (
  start: number,
  end: number,
  duration: number,
): { start: number; end: number; error?: string } => {
  if (duration <= 0) return { start, end };
  if (start >= duration) {
    return {
      start, end,
      error: `Part start (${formatMmSs(start)}) is past the end of the audio (${formatMmSs(duration)}). Edit the part in the Tracks tab.`,
    };
  }
  if (end <= start) {
    return { start, end, error: 'Part stop time must be greater than its start time.' };
  }
  // Clamp end to the audio duration if it overruns.
  const clampedEnd = Math.min(end, duration);
  return { start, end: clampedEnd };
};

const Player: React.FC<PlayerProps> = ({ tracks }) => {
  const dispatch = useAppDispatch();
  const { mode, isPlaying, currentBpm, backingTrack, metronome, timerRemaining } = useAppSelector((state) => state.app);

  const youtubeEngineRef = useRef<YouTubeEngine | null>(null);
  const audioEngineRef = useRef<AudioEngine | null>(null);
  const metronomeEngineRef = useRef<MetronomeEngine | null>(null);
  const currentBpmRef = useRef<number>(currentBpm);

  const [error, setError] = useState<string | null>(null);
  const [currentTrack, setCurrentTrack] = useState<Track | null>(null);
  const [ytPlayerReady, setYtPlayerReady] = useState(false);
  const [ytMaximized, setYtMaximized] = useState(false);

  // Keep the BPM ref in sync with state (used inside loop callbacks)
  useEffect(() => {
    currentBpmRef.current = currentBpm;
  }, [currentBpm]);

  // Auto-restore the YouTube video to its normal size whenever playback stops
  useEffect(() => {
    if (!isPlaying && ytMaximized) {
      setYtMaximized(false);
    }
  }, [isPlaying, ytMaximized]);

  // Allow Escape to exit the maximized YouTube view
  useEffect(() => {
    if (!ytMaximized) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setYtMaximized(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ytMaximized]);

  // Stop playback when timer expires
  useEffect(() => {
    if (isPlaying && timerRemaining === 0) {
      if (mode === 'backingTrack') {
        youtubeEngineRef.current?.stop();
        audioEngineRef.current?.stop();
      } else {
        metronomeEngineRef.current?.stop();
      }
      dispatch(setIsPlaying(false));
    }
  }, [isPlaying, timerRemaining, mode, dispatch]);

  // Dispose engines on unmount
  useEffect(() => {
    return () => {
      youtubeEngineRef.current?.dispose();
      audioEngineRef.current?.dispose();
      metronomeEngineRef.current?.dispose();
    };
  }, []);

  // When selected track changes, reset the YouTube engine and player ready state
  useEffect(() => {
    if (backingTrack.selectedTrackId && tracks.length > 0) {
      const track = tracks.find((t) => t.id === backingTrack.selectedTrackId);

      // Stop any ongoing playback before switching
      if (isPlaying) {
        youtubeEngineRef.current?.stop();
        audioEngineRef.current?.stop();
        dispatch(setIsPlaying(false));
      }

      youtubeEngineRef.current?.dispose();
      youtubeEngineRef.current = new YouTubeEngine();
      audioEngineRef.current?.dispose();
      audioEngineRef.current = null;
      setYtPlayerReady(false);
      setCurrentTrack(track || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backingTrack.selectedTrackId, tracks]);

  // Reflect the selected part's starting BPM in the "Current BPM" display
  // whenever the selection changes and we're not actively playing.
  useEffect(() => {
    if (mode !== 'backingTrack' || isPlaying) return;
    if (!currentTrack || backingTrack.selectedPartIndex === null) return;
    const part = currentTrack.metadata.parts[backingTrack.selectedPartIndex];
    if (!part) return;
    const baseBpm = part.bpmOverride || currentTrack.metadata.bpm;
    const partSettings =
      backingTrack.selectedTrackId
        ? backingTrack.partSettings[backingTrack.selectedTrackId]?.[backingTrack.selectedPartIndex]
        : undefined;
    const displayBpm = partSettings?.autoSpeedUp
      ? partSettings.startBpm
      : (partSettings?.fixedBpm ?? baseBpm);
    if (displayBpm !== currentBpmRef.current) {
      dispatch(setCurrentBpm(displayBpm));
    }
  }, [
    mode,
    isPlaying,
    currentTrack,
    backingTrack.selectedTrackId,
    backingTrack.selectedPartIndex,
    backingTrack.partSettings,
    dispatch,
  ]);

  // Live-apply Fixed BPM slider changes during playback.
  useEffect(() => {
    if (mode !== 'backingTrack' || !isPlaying) return;
    if (!currentTrack || backingTrack.selectedPartIndex === null) return;
    const part = currentTrack.metadata.parts[backingTrack.selectedPartIndex];
    if (!part) return;
    const settings = backingTrack.selectedTrackId
      ? backingTrack.partSettings[backingTrack.selectedTrackId]?.[backingTrack.selectedPartIndex]
      : undefined;
    if (!settings || settings.autoSpeedUp) return;
    const baseBpm = part.bpmOverride || currentTrack.metadata.bpm;
    const targetBpm = settings.fixedBpm ?? baseBpm;
    if (targetBpm === currentBpmRef.current) return;
    if (currentTrack.youtubeId) {
      youtubeEngineRef.current?.setPlaybackRate(targetBpm, baseBpm);
    } else {
      audioEngineRef.current?.setPlaybackRate(targetBpm, baseBpm);
    }
    dispatch(setCurrentBpm(targetBpm));
  }, [
    mode,
    isPlaying,
    currentTrack,
    backingTrack.selectedTrackId,
    backingTrack.selectedPartIndex,
    backingTrack.partSettings,
    dispatch,
  ]);

  // ── YouTube player callbacks ─────────────────────────────────────────────

  const handleYouTubeReady = (player: YT.Player) => {
    youtubeEngineRef.current?.attachPlayer(player);
    setYtPlayerReady(true);
  };

  const handleYouTubeError = (errorCode: any) => {
    setError(`YouTube player error (code ${errorCode}). The video may be unavailable or restricted.`);
  };

  // ── Play / Stop ──────────────────────────────────────────────────────────

  const handlePlayPause = () => {
    setError(null);

    if (isPlaying) {
      if (mode === 'backingTrack') {
        youtubeEngineRef.current?.stop();
        audioEngineRef.current?.stop();
      } else {
        metronomeEngineRef.current?.stop();
      }
      dispatch(setIsPlaying(false));
      return;
    }

    if (mode === 'backingTrack') {
      handleBackingTrackPlay();
    } else {
      handleMetronomePlay();
    }
  };

  const handleBackingTrackPlay = async () => {
    if (!currentTrack || backingTrack.selectedPartIndex === null || !backingTrack.selectedTrackId) {
      setError('Please select a track and part');
      return;
    }

    const part = currentTrack.metadata.parts[backingTrack.selectedPartIndex];
    if (!part) {
      setError('Invalid part selected');
      return;
    }

    const partSettings = backingTrack.partSettings[backingTrack.selectedTrackId]?.[backingTrack.selectedPartIndex];
    const settings = {
      ...(partSettings || {
        autoSpeedUp: false,
        startBpm: 60,
        targetBpm: 120,
        increaseBpm: 5,
        pitchShift: 0,
        duration: 300,
      }),
    };

    const baseBpm = part.bpmOverride || currentTrack.metadata.bpm;
    let startBpm = settings.autoSpeedUp
      ? settings.startBpm
      : (settings.fixedBpm ?? baseBpm);

    // For YouTube tracks, snap the starting BPM (and target BPM in auto-speed-up)
    // to the nearest supported playback rate so playback uses actually playable speeds.
    if (currentTrack.youtubeId) {
      const snappedStart = Math.round(baseBpm * snapToYouTubeRate(startBpm / baseBpm));
      const snappedTarget = settings.autoSpeedUp
        ? Math.round(baseBpm * snapToYouTubeRate(settings.targetBpm / baseBpm))
        : settings.targetBpm;
      const startChanged = snappedStart !== startBpm;
      const targetChanged = settings.autoSpeedUp && snappedTarget !== settings.targetBpm;
      if (startChanged) startBpm = snappedStart;
      if ((startChanged || targetChanged) && settings.autoSpeedUp && backingTrack.selectedTrackId !== null) {
        const patch: Partial<typeof settings> = {};
        if (startChanged) patch.startBpm = snappedStart;
        if (targetChanged) {
          patch.targetBpm = snappedTarget;
          settings.targetBpm = snappedTarget;
        }
        dispatch(updatePartSettings({
          trackId: backingTrack.selectedTrackId,
          partIndex: backingTrack.selectedPartIndex,
          settings: patch,
        }));
      }
    }

    if (currentTrack.youtubeId) {
      // ── YouTube path ────────────────────────────────────────────────────
      if (!ytPlayerReady || !youtubeEngineRef.current?.isReady()) {
        setError('YouTube player is not ready yet — please wait a moment and try again.');
        return;
      }

      const ytDuration = youtubeEngineRef.current!.getDuration();
      const partRange = validatePartRange(part.startTime, part.stopTime, ytDuration);
      if (partRange.error) { setError(partRange.error); return; }

      dispatch(setCurrentBpm(startBpm));

      const engine = youtubeEngineRef.current!;
      engine.setPlaybackRate(startBpm, baseBpm);
      engine.setLoop(partRange.start, partRange.end, () => {
        if (settings.autoSpeedUp) {
          handleSpeedIncrease(baseBpm, settings);
        }
      });
      engine.play(partRange.start);
    } else {
      // ── MP3 fallback path ────────────────────────────────────────────────────
      if (!currentTrack.audioFile) {
        setError('No audio source available for this track.');
        return;
      }

      try {
        if (!audioEngineRef.current) {
          audioEngineRef.current = new AudioEngine();
        }

        // Prefer the locally-stored blob (IndexedDB) so the user doesn't need
        // to copy the file into public/tracks/ on every device.
        let audioUrl = currentTrack.audioFile;
        const localEntry = backingTrack.selectedTrackId
          ? await localFileStore.getFileEntry(backingTrack.selectedTrackId)
          : null;
        let objectUrl: string | undefined;
        if (localEntry) {
          objectUrl = URL.createObjectURL(localEntry.blob);
          audioUrl = objectUrl;
        }

        console.debug('[Player] loading MP3:', audioUrl, localEntry ? '(from IndexedDB)' : '(from public/tracks/)');
        // Hand the object URL to the engine so it stays alive while the buffer
        // is in use; the engine will revoke it on next load/dispose.
        await audioEngineRef.current.loadTrack(audioUrl, objectUrl);

        const audioDuration = audioEngineRef.current.getDuration();
        const partRange = validatePartRange(part.startTime, part.stopTime, audioDuration);
        if (partRange.error) { setError(partRange.error); return; }

        dispatch(setCurrentBpm(startBpm));
        audioEngineRef.current.setPlaybackRate(startBpm, baseBpm);
        audioEngineRef.current.setPitchShift(settings.pitchShift);
        audioEngineRef.current.setLoop(partRange.start, partRange.end, () => {
          if (settings.autoSpeedUp) {
            handleSpeedIncrease(baseBpm, settings);
          }
        });
        audioEngineRef.current.play(partRange.start);
      } catch (err) {
        const rawPath = currentTrack?.audioFile ?? '';
        let fileName = '(unknown)';
        if (rawPath) {
          const lastSlash = Math.max(rawPath.lastIndexOf('/'), rawPath.lastIndexOf('\\'));
          const base = lastSlash >= 0 ? rawPath.slice(lastSlash + 1) : rawPath;
          try { fileName = decodeURIComponent(base); } catch { fileName = base; }
        }
        const detail = err instanceof Error ? err.message : String(err);
        setError(`Failed to load MP3: ${fileName} — ${detail}. If this is a new device, you will need to reupload mp3 file.`);
        console.error('[Player] loadTrack error for', rawPath, err);
        return;
      }
    }

    dispatch(setIsPlaying(true));
  };

  const handleSpeedIncrease = (baseBpm: number, settings: { increaseBpm: number; targetBpm: number }) => {
    const desiredBpm = currentBpmRef.current + settings.increaseBpm;
    let newBpm: number;
    if (currentTrack?.youtubeId) {
      // YouTube only supports a fixed set of playback rates — snap the
      // increment up to the next BPM that maps to a higher supported rate.
      const snapped = nextYouTubeBpm(currentBpmRef.current, desiredBpm, baseBpm);
      if (snapped === null) return; // already at YouTube's max rate
      newBpm = snapped;
    } else {
      newBpm = desiredBpm;
    }
    if (newBpm <= settings.targetBpm) {
      dispatch(setCurrentBpm(newBpm));
      if (currentTrack?.youtubeId) {
        youtubeEngineRef.current?.setPlaybackRate(newBpm, baseBpm);
      } else {
        audioEngineRef.current?.setPlaybackRate(newBpm, baseBpm);
      }
    }
  };

  // ── Metronome ────────────────────────────────────────────────────────────

  const handleMetronomePlay = () => {
    if (!metronomeEngineRef.current) {
      metronomeEngineRef.current = new MetronomeEngine();
    }

    const startBpm = metronome.autoSpeedUp
      ? metronome.startBpm
      : (metronome.fixedBpm ?? metronome.startBpm);
    dispatch(setCurrentBpm(startBpm));

    metronomeEngineRef.current.start(
      startBpm,
      metronome.beatsBeforeIncrease,
      undefined,
      () => {
        if (metronome.autoSpeedUp) {
          handleMetronomeSpeedIncrease();
        }
      },
      metronome.timeSignature,
    );

    dispatch(setIsPlaying(true));
  };

  const handleMetronomeSpeedIncrease = () => {
    const newBpm = currentBpmRef.current + metronome.increaseBpm;
    if (newBpm <= metronome.targetBpm) {
      dispatch(setCurrentBpm(newBpm));
      metronomeEngineRef.current?.setBpm(newBpm, metronome.timeSignature.beatUnit);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  const canPlay =
    mode === 'backingTrack'
      ? currentTrack && backingTrack.selectedPartIndex !== null &&
        (currentTrack.youtubeId ? ytPlayerReady : true)
      : true;

  const showYouTubePlayer = mode === 'backingTrack' && currentTrack?.youtubeId;

  return (
    <div className="player text-center">
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      <div className="d-flex align-items-center justify-content-center gap-3 mb-2 flex-wrap text-center">
        <Button
          variant={isPlaying ? 'danger' : 'success'}
          size="lg"
          onClick={handlePlayPause}
          disabled={!canPlay}
        >
          {isPlaying ? 'Stop' : 'Play'}
        </Button>

        {mode !== 'backingTrack' ? (
          <div className="current-bpm">
            <strong>Current BPM:</strong> <span className="fs-4 text-primary">{currentBpm}</span>
          </div>
        ) : (
          backingTrack.selectedTrackId && backingTrack.selectedPartIndex !== null && (
            <div className="current-bpm">
              <strong>Current BPM:</strong> <span className="fs-4 text-primary">{currentBpm}</span>
            </div>
          )
        )}
      </div>

      {isPlaying && mode === 'backingTrack' && backingTrack.selectedTrackId && backingTrack.selectedPartIndex !== null && (() => {
        const mins = Math.floor(timerRemaining / 60).toString().padStart(2, '0');
        const secs = (timerRemaining % 60).toString().padStart(2, '0');
        return (
          <div className={`fs-5 mb-3 text-center ${timerRemaining <= 10 ? 'text-danger fw-bold' : 'text-success'}`}>
            <strong>Time Remaining:</strong> {mins}:{secs}
          </div>
        );
      })()}

      {/* Fixed BPM slider — quick access when not in auto-speed-up mode */}
      {(() => {
        if (mode === 'backingTrack') {
          if (!currentTrack || backingTrack.selectedPartIndex === null) return null;
          const settings = backingTrack.partSettings[backingTrack.selectedTrackId!]?.[backingTrack.selectedPartIndex];
          if (settings?.autoSpeedUp) return null;

          const part = currentTrack.metadata.parts[backingTrack.selectedPartIndex];
          const baseBpm = part?.bpmOverride || currentTrack.metadata.bpm;
          const isYouTubeTrack = !!currentTrack.youtubeId;
          const currentFixed = settings?.fixedBpm ?? baseBpm;

          const applyBpm = (bpm: number) => {
            dispatch(updatePartSettings({
              trackId: backingTrack.selectedTrackId!,
              partIndex: backingTrack.selectedPartIndex!,
              settings: { fixedBpm: bpm },
            }));
            dispatch(setCurrentBpm(bpm));
            if (isPlaying) {
              if (isYouTubeTrack) {
                youtubeEngineRef.current?.setPlaybackRate(bpm, baseBpm);
              } else {
                audioEngineRef.current?.setPlaybackRate(bpm, baseBpm);
              }
            }
          };

          if (isYouTubeTrack) {
            const available = YOUTUBE_RATES.map((r) => Math.round(baseBpm * r));
            let closestIdx = 0;
            let closestDiff = Infinity;
            available.forEach((b, i) => {
              const d = Math.abs(b - currentFixed);
              if (d < closestDiff) { closestDiff = d; closestIdx = i; }
            });
            const displayBpm = available[closestIdx];
            return (
              <>
                <Form.Group className="mb-3 mx-auto" style={{ maxWidth: 480 }}>
                  <Form.Label className="d-flex align-items-center gap-2">
                    <span>BPM: {displayBpm} (track: {baseBpm})</span>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => applyBpm(baseBpm)}
                      disabled={displayBpm === baseBpm}
                    >
                      Reset
                    </Button>
                  </Form.Label>
                  <Form.Range
                    value={closestIdx}
                    onChange={(e) => applyBpm(available[parseInt(e.target.value, 10)])}
                    min={0}
                    max={available.length - 1}
                    step={1}
                  />
                </Form.Group>
                <div className="mb-3 p-2 border rounded bg-light">
                  <p className="text-muted small mb-0">
                    <strong>YouTube available BPMs for this track:</strong>{' '}
                    {available.join(', ')} BPM
                    <p className="text-info">
                      For more accurate speeds, add a local MP3 file
                    </p>
                  </p>
                </div>
              </>
            );
          }

          const minBpm = Math.max(20, Math.round(baseBpm * 0.25));
          const maxBpm = Math.min(300, Math.round(baseBpm * 2));
          return (
            <Form.Group className="mb-3 mx-auto" style={{ maxWidth: 480 }}>
              <Form.Label className="d-flex align-items-center gap-2">
                <span>BPM: {currentFixed} (track: {baseBpm})</span>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => applyBpm(baseBpm)}
                  disabled={currentFixed === baseBpm}
                >
                  Reset
                </Button>
              </Form.Label>
              <Form.Range
                value={currentFixed}
                onChange={(e) => applyBpm(parseInt(e.target.value, 10))}
                min={minBpm}
                max={maxBpm}
                step={1}
              />
            </Form.Group>
          );
        }

        if (mode === 'metronome' && !metronome.autoSpeedUp) {
          const currentFixed = metronome.fixedBpm ?? metronome.startBpm;
          const applyBpm = (bpm: number) => {
            dispatch(updateMetronomeSettings({ fixedBpm: bpm }));
            dispatch(setCurrentBpm(bpm));
            if (isPlaying) {
              metronomeEngineRef.current?.setBpm(bpm, metronome.timeSignature.beatUnit);
            }
          };
          return (
            <Form.Group className="mb-3 mx-auto" style={{ maxWidth: 480 }}>
              <Form.Label>BPM: {currentFixed}</Form.Label>
              <Form.Range
                value={currentFixed}
                onChange={(e) => applyBpm(parseInt(e.target.value, 10))}
                min={20}
                max={300}
                step={1}
              />
            </Form.Group>
          );
        }

        return null;
      })()}

      {/* YouTube embedded player — shown when a track with a YouTube ID is selected */}
      {showYouTubePlayer && (
        <div className="mb-3">
          <YouTubePlayer
            key={currentTrack!.youtubeId}
            videoId={currentTrack!.youtubeId!}
            onReady={handleYouTubeReady}
            onError={handleYouTubeError}
            maximized={ytMaximized}
          />
          {!ytPlayerReady && (
            <small className="text-muted">Loading YouTube player…</small>
          )}
          <div className="mt-2">
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => setYtMaximized((m) => !m)}
              disabled={!ytPlayerReady}
            >
              {ytMaximized ? 'Restore video' : 'Maximize video'}
            </Button>
          </div>
          {ytMaximized && (
            <Button
              variant="light"
              onClick={() => setYtMaximized(false)}
              style={{
                position: 'fixed',
                top: 12,
                right: 12,
                zIndex: 1060,
              }}
            >
              Restore video
            </Button>
          )}
        </div>
      )}      
    </div>
  );
};

export default Player;
